// The PUBLIC_LEDGER team's card when a company has had this month's nodeStream-paid public ledger fee top-ups and a change
// needs one more (#25 phase 6, round 6): what autopay/seed-fee wrote on the job. Pure, so the unit tests import it.
import { formatCredits } from '../../../../helpers/public-ledger-credits.js';

type Metadata = ReadonlyArray<{ key?: string | null; value?: string | null } | null | undefined> | null | undefined;

// The most fee top-ups nodeStream pays a company in a UK month (the lambda's PLATFORM_SEED_CAP; round 5 follow-ups)
export const PLATFORM_SEED_CAP = 5;

// The last entry of a key, as the engine reads it; null when absent or blank
const text = (metadata: Metadata, key: string): string | null => {
  const value = [...(metadata ?? [])].reverse().find((m) => m?.key === key)?.value;
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
};

const credits = (value: string | null): string | null =>
  value !== null && /^\d+$/.test(value) ? `${formatCredits(Number(value))} credits` : null;

// The card's intro, with the company's name (approved, round 5 follow-ups)
export function feeOverCapIntro(companyName: string): string {
  return `${companyName} has had ${PLATFORM_SEED_CAP} public ledger fee top-ups paid by nodeStream this month, the most allowed. This change needs one more to go ahead.`;
}

// What the card tells staff the buttons do (approved, round 5 follow-ups)
export const FEE_OVER_CAP_TEXT =
  "Pay this fee sends one top-up and the change goes ahead. Don't pay cancels the change and tells the requester.";

// The buttons (approved, round 5 follow-ups)
export const PAY_THIS_FEE = 'Pay this fee';
export const DONT_PAY = "Don't pay";

// Round 10: the only button on a covered card (job outcome covered; approved, round 10 follow-ups)
export const COMPLETE_COVERED = 'Complete';

// Round 15: a card change's seeds (its record's schedule and last signature) are CARD
export type SeedKind = 'CHANGE' | 'SIGN' | 'CARD';

// The "What's waiting" row's value for each kind of change (approved, round 6 follow-ups)
const WAITING: Record<SeedKind, string> = {
  CHANGE: 'A change of automatic payment amounts, before its signatures are asked for',
  SIGN: 'A change of automatic payment amounts, at its last signature',
  CARD: 'A change of the automatic payment card',
};

export interface FeeOverCap {
  companyName: string;
  kind: SeedKind | null;
  requestedBy: string | null;
  // What the company's operating account lacks, in whole credits, as "151 credits"
  feeNeeded: string | null;
  seedsThisMonth: string | null;
  // The operating account's balance, null when it couldn't be read
  operatingBalance: string | null;
  // Round 10: the company's own operating account paid the fee after all (COVERED 'true'): nothing to pay or decline
  covered: boolean;
}

export function feeOverCap(metadata: Metadata): FeeOverCap {
  const kind = text(metadata, 'SEED_KIND');
  const seeds = text(metadata, 'SEEDS_THIS_MONTH');
  return {
    companyName: text(metadata, 'SUBJECT_COMPANY_NAME') ?? text(metadata, 'SUBJECT_COMPANY_ID') ?? 'The company',
    kind: kind === 'CHANGE' || kind === 'SIGN' || kind === 'CARD' ? kind : null,
    requestedBy: text(metadata, 'REQUESTED_BY_NAME'),
    feeNeeded: credits(text(metadata, 'FEE_NEEDED_CREDITS')),
    seedsThisMonth: seeds !== null && /^\d+$/.test(seeds) ? seeds : null,
    operatingBalance: credits(text(metadata, 'OPERATING_BALANCE_CREDITS')),
    covered: text(metadata, 'COVERED') === 'true',
  };
}

export interface CoveredReason {
  heading: string;
  text: string;
}

// Round 11: what a covered card says (job metadata COVERED 'true'): why it was raised and why it's no longer needed, so
// staff just complete it (approved, round 10 follow-ups). What's waiting is the row's value, lower-cased
export function feeOverCapCoveredText(fee: FeeOverCap): CoveredReason[] {
  // With no kind, "this change"; with no requester, the ", requested by" part left out (approved, round 14+15 follow-ups);
  // the server always writes both
  const waiting = fee.kind ? WAITING[fee.kind].replace(/^./, (first) => first.toLowerCase()) : 'this change';
  const requested = fee.requestedBy ? `, requested by ${fee.requestedBy}` : '';
  return [
    {
      heading: 'Why this was raised:',
      text: `${fee.companyName} couldn't pay the public ledger fee for ${waiting}${requested}. Its company account had no credits, its automatic payment card couldn't be charged, and it had used this month's ${PLATFORM_SEED_CAP} nodeStream-paid top-ups.`,
    },
    {
      heading: "Why it's no longer needed:",
      text: `${fee.companyName}'s operating account has since received funds and paid the fee itself, so the change went ahead without a top-up from nodeStream. There's nothing to pay: complete this job.`,
    },
  ];
}

// The card's rows (approved labels, round 5 follow-ups)
export function feeOverCapRows(fee: FeeOverCap): { label: string; value: string | undefined }[] {
  return [
    { label: 'Company', value: fee.companyName },
    { label: "What's waiting", value: fee.kind ? WAITING[fee.kind] : undefined },
    { label: 'Requested by', value: fee.requestedBy ?? undefined },
    { label: 'Fee needed', value: fee.feeNeeded ?? undefined },
    { label: 'Top-ups paid this month', value: fee.seedsThisMonth ?? undefined },
    // Approved (round 4 follow-ups, small string 6)
    { label: 'Operating account balance', value: fee.operatingBalance ?? "Can't be read right now" },
  ];
}
